import { createSelector } from '@reduxjs/toolkit';
import { mean } from 'lodash';
import { DefaultRootState } from 'react-redux';
import { getSortingStrategy } from '../clustering/client/helpers';
import { ClusterBeat } from '../clustering/clustering.types';
import { sortingStrategySelector } from '../layout/selectors/layout.selectors';
import { selectAll, selectById } from './beats.reducer';

const beatsStateSelector = ({ beats }: DefaultRootState) => beats;

export const beatsSelector = createSelector(beatsStateSelector, selectAll);

export const beatIdsSelector = createSelector(beatsSelector, (beats) =>
  beats.map((beat) => beat.id),
);

export const pinnedIdSelector = createSelector(
  beatsStateSelector,
  ({ pinnedId }) => pinnedId,
);

export const selectedBeatIdsSelector = createSelector(
  beatsStateSelector,
  ({ selectedIds }) => selectedIds,
);

export const hoveredIdSelector = createSelector(
  beatsStateSelector,
  ({ hoveredId }) => hoveredId,
);

export const isBeatSelectedSelector = (beatId: number) =>
  createSelector(selectedBeatIdsSelector, (selectedIds) =>
    selectedIds.has(beatId),
  );

export const pinnedBeatSelector = createSelector(
  beatsStateSelector,
  pinnedIdSelector,
  (state, pinnedId) =>
    pinnedId != null ? selectById(state, pinnedId) : undefined,
);

export const isBeatHoveredSelector = (beatId: number) =>
  createSelector(
    hoveredIdSelector,
    (hoveredBeatId) => hoveredBeatId === beatId,
  );

export const hoveredBeatSelector = createSelector(
  beatsStateSelector,
  hoveredIdSelector,
  (state, hoveredId) =>
    hoveredId != null ? selectById(state, hoveredId) : undefined,
);
export const sortedBeatsSelector = createSelector(
  beatsSelector,
  sortingStrategySelector,
  pinnedBeatSelector,
  (beats, sortingStrategy, pinnedBeat) => {
    const sortedBeats = [...beats];
    return sortedBeats.sort(getSortingStrategy(sortingStrategy, pinnedBeat));
  },
);

export const sortedBeatIdsSelector = createSelector(
  sortedBeatsSelector,
  (beats) => beats.map((beat) => beat.id),
);

export const getGroupedBeatsSelector = createSelector(
  beatsSelector,
  selectedBeatIdsSelector,
  (beats, selectedBeatIds) => {
    const selected: ClusterBeat[] = [];
    const unselected: ClusterBeat[] = [];
    beats.forEach((beat) => {
      if (selectedBeatIds.has(beat.id)) {
        selected.push(beat);
      } else {
        unselected.push(beat);
      }
    });
    return { selected, unselected };
  },
);

export const meanClusterOnsetOffsetDurationSelector = createSelector(
  beatsSelector,
  (beats) => mean(beats.map((beat) => beat.offset - beat.onset)),
);
